import '@elfalem/leaflet-curve';

import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Curve, curve, latLng, LayerGroup, layerGroup, LeafletMouseEvent } from 'leaflet';
import { filter, firstValueFrom, takeUntil } from 'rxjs';
import { Season } from 'src/app/core/domain/season.enum';
import { parseCurveCoords } from 'src/app/core/leaflet-helper/curve-coords';
import { Unsubscriber } from 'src/app/core/unsubscriber/unsubscriber';
import { ScreenSizeService } from 'src/app/core/utils/screen-size.service';
import { AssetStatus } from 'src/app/maps/domain/livedata/status.enum';
import { Lift } from 'src/app/maps/domain/masterdata/lift.model';
import { MapElement } from 'src/app/maps/domain/masterdata/map-element.model';
import { MapPosition } from 'src/app/maps/domain/masterdata/map-position.model';
import { MasterDataService } from 'src/app/maps/domain/masterdata/masterdata.service';
import { SisMapAssetCategory } from 'src/app/maps/domain/masterdata/sismap-asset-category.enum';
import { HoverService } from 'src/app/maps/map/hover.service';
import { LayerService } from 'src/app/maps/map/layer-selector/layer.service';
import { MapStateService } from 'src/app/maps/map/map-state.service';
import { CurveEndMarkers } from 'src/app/maps/map/style/curve-end-markers.model';
import { MapStyleService } from 'src/app/maps/map/style/map-style.service';
import { SisZoom } from 'src/app/maps/map/zoom.model';
import { ZoomService } from 'src/app/maps/map/zoom.service';

@Component({
  selector: 'sis-map-element-curve',
  template: '',
  styleUrls: ['./map-element-curve.component.scss'],
  standalone: false,
})
export class MapElementCurveComponent extends Unsubscriber implements OnInit {
  @Input() element: MapElement;
  @Input() assetsLayerGroup: LayerGroup;
  @Input() showStatus: boolean;
  @Input() editMode: boolean;

  private readonly clickable = '-clickable';
  private readonly highlight = '-highlight';

  private visibleCurve: Curve;
  private highlightCurve: Curve;
  private clickableCurve: Curve;
  private backgroundCurve: Curve;
  private isMarked = false;
  private bigScreenMode = false;
  private layerGroup: LayerGroup;

  private curveEndMarkers: CurveEndMarkers;

  constructor(
    private zoomService: ZoomService,
    private hoverService: HoverService,
    private layerService: LayerService,
    private mapStateService: MapStateService,
    private mapStyleService: MapStyleService,
    private changeDetector: ChangeDetectorRef,
    private masterDataService: MasterDataService,
    private screenSizeService: ScreenSizeService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.element?.path) {
      this.initCurveLayer();
      this.layerService.season$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.updateCurve();
      });
      this.mapStateService.elementUpdated$
        .pipe(
          filter((e) => e.guid === this.element.guid),
          takeUntil(this.onDestroy$),
        )
        .subscribe(() => this.updateCurve());
    }

    this.mapStateService.selectedElement$.pipe(takeUntil(this.onDestroy$)).subscribe(({ element }) => {
      if (element?.guid === this.element.guid) {
        this.isMarked = true;
      } else if (this.isMarked) {
        this.isMarked = false;
      }
      this.updateCurve();
    });

    this.screenSizeService.bigScreenMode$.pipe(takeUntil(this.onDestroy$)).subscribe((bigScreenMode) => {
      this.bigScreenMode = bigScreenMode;
    });
  }

  private async initCurveLayer(): Promise<void> {
    this.layerGroup = layerGroup([]);

    this.assetsLayerGroup.addLayer(this.layerGroup);

    const coords = parseCurveCoords(this.element.path) ?? [];

    const color = await this.getPathColor(this.element.status);
    const className = `sis-${SisMapAssetCategory[this.element.category].toLowerCase()}-curve`;
    const pathWeight = this.element.pathWeight ?? this.element.defaultPathWeight;
    const dashArray = this.element.pathDashArray?.split(' ').map((x) => +x);
    const lineCap = this.element.pathBackgroundColor ? 'square' : 'round';

    if (this.element.category === SisMapAssetCategory.Lift) {
      this.backgroundCurve = curve(coords, {
        color: this.element.pathBackgroundColor,
        weight: pathWeight,
        opacity: this.element.pathBackgroundColor ? 1 : 0,
        className,
        lineCap: 'round',
      });
      this.layerGroup.addLayer(this.backgroundCurve);
    }

    this.visibleCurve = curve(coords, {
      interactive: true,
      color,
      weight: pathWeight,
      dashArray,
      className,
      lineCap,
    });
    this.layerGroup.addLayer(this.visibleCurve);

    this.highlightCurve = curve(coords, {
      color,
      weight: pathWeight * 2.5,
      className: className + this.highlight,
      lineCap: 'round',
    });
    this.layerGroup.addLayer(this.highlightCurve);

    this.clickableCurve = curve(coords, {
      interactive: true,
      bubblingMouseEvents: false,
      fill: false,
      weight: pathWeight * 2.5,
      opacity: 0,
      className: className + this.clickable,
    });
    this.layerGroup.addLayer(this.clickableCurve);

    this.curveEndMarkers = await this.mapStyleService.createCurveEndMarkers(coords, color, this.element, '');
    this.curveEndMarkers.visible.forEach((l) => this.layerGroup.addLayer(l));
    this.curveEndMarkers.highlight.forEach((l) => this.layerGroup.addLayer(l));

    this.addEventListeners();

    this.layerService.shownAssetGuids$.pipe(takeUntil(this.onDestroy$)).subscribe((shownAssetGuids) => {
      if (shownAssetGuids.includes(this.element.guid)) {
        this.showLayer();
      } else {
        this.hideLayer();
      }
    });

    this.zoomService.zoomChanged$.pipe(takeUntil(this.onDestroy$)).subscribe((zoom) => {
      this.zoom(zoom);
    });

    this.hoverService.onHover.pipe(takeUntil(this.onDestroy$)).subscribe((hoverData) => {
      if (this.assetsLayerGroup.hasLayer(this.layerGroup)) {
        if (hoverData.guid === this.element.guid && hoverData.hover) {
          if (!this.element.disallowHighlight) {
            if (this.curveExists(this.highlightCurve)) {
              this.setStrokeOpacity(this.highlightCurve, 0.5);
            }
            this.mapStyleService.setHighlight(this.curveEndMarkers, true);
          }
          if (!hoverData.iconHovered && !this.element.disallowPopUp && this.bigScreenMode) {
            this.createAndShowPopup(hoverData.hoverPosition);
          }
        } else {
          if (!this.isMarked) {
            if (this.curveExists(this.highlightCurve)) {
              this.setStrokeOpacity(this.highlightCurve, 0);
            }
            this.mapStyleService.setHighlight(this.curveEndMarkers, false);
          }

          if (this.bigScreenMode) {
            this.visibleCurve.closePopup();
          }
        }
      }
    });

    this.updateCurve();
  }

  private async createAndShowPopup(position: MapPosition): Promise<void> {
    if (this.visibleCurve == null) {
      return;
    }

    const masterData = await firstValueFrom(this.masterDataService.masterData);
    const { popupContent, className } = await this.mapStyleService.createPopupContent(
      this.element,
      masterData.labelInTooltip,
    );

    this.visibleCurve.bindPopup(popupContent, {
      closeButton: false,
      offset: [0, 0],
      className: `sis-map-tooltip ${className}`,
      autoPan: false,
      maxWidth: 1000,
    });
    this.visibleCurve.openPopup(position != null ? latLng(position.x, position.y) : undefined);
  }

  private async updateCurve(): Promise<void> {
    if (this.visibleCurve) {
      const pathWeight = this.element.pathWeight ?? this.element.defaultPathWeight;
      const dashArray = this.element.pathDashArray?.split(' ').map((x) => +x);
      const lineCap = this.element.pathBackgroundColor ? 'square' : 'round';
      const color = await this.getPathColor(this.element.status);
      const coords = parseCurveCoords(this.element.path);
      const className = `sis-${SisMapAssetCategory[this.element.category].toLowerCase()}-curve`;

      if (coords == null) {
        return;
      }

      let pane = this.element.status.toString();
      if (this.element.category === SisMapAssetCategory.Lift) {
        pane = SisMapAssetCategory.Lift.toString() + pane;
      }

      if (this.element.category === SisMapAssetCategory.Lift) {
        this.layerGroup.removeLayer(this.backgroundCurve);
        this.backgroundCurve.setPath(coords);
        this.backgroundCurve.setStyle({
          color: this.element.pathBackgroundColor,
          weight: pathWeight,
          opacity: this.element.pathBackgroundColor ? 1 : 0,
          className,
          lineCap,
          pane,
        });
        this.layerGroup.addLayer(this.backgroundCurve);
      }

      this.layerGroup.removeLayer(this.visibleCurve);
      this.visibleCurve.setPath(coords);
      this.visibleCurve.setStyle({
        color,
        weight: pathWeight,
        dashArray,
        className,
        lineCap,
        pane,
      });
      this.layerGroup.addLayer(this.visibleCurve);

      this.layerGroup.removeLayer(this.highlightCurve);
      this.highlightCurve.setPath(coords);
      this.highlightCurve.setStyle({
        color,
        weight: pathWeight * 2.5,
        opacity: (!this.element.disallowHighlight || this.editMode) && this.isMarked ? 0.5 : 0,
        className: className + this.highlight,
        lineCap: 'round',
        pane,
      });
      this.layerGroup.addLayer(this.highlightCurve);

      this.layerGroup.removeLayer(this.clickableCurve);
      this.clickableCurve.setPath(coords);
      this.clickableCurve.setStyle({
        weight: pathWeight * 2.5,
        className: className + this.clickable,
        pane,
      });
      this.layerGroup.addLayer(this.clickableCurve);

      this.curveEndMarkers.visible.forEach((l) => this.layerGroup.removeLayer(l));
      this.curveEndMarkers.highlight.forEach((l) => this.layerGroup.removeLayer(l));
      this.curveEndMarkers = await this.mapStyleService.createCurveEndMarkers(coords, color, this.element, pane);
      this.curveEndMarkers.visible.forEach((l) => this.layerGroup.addLayer(l));
      this.curveEndMarkers.highlight.forEach((l) => this.layerGroup.addLayer(l));

      await this.mapStyleService.setHighlight(
        this.curveEndMarkers,
        (!this.element.disallowHighlight || this.editMode) && this.isMarked,
      );

      const zoom = await firstValueFrom(this.zoomService.zoomChanged$);
      await this.zoom(zoom);
    }
  }

  private async getPathColor(status: AssetStatus): Promise<string> {
    if (this.element instanceof Lift) {
      const liftColor = await this.mapStyleService.getLiftColor(
        this.showStatus ? this.element.status : AssetStatus.Disabled,
        this.element,
      );
      return liftColor.statusColor;
    }

    interface SeasonalColor { summer: string; winter: string }

    const rawPathColor: string | SeasonalColor | null | undefined =
      this.element.pathColor ?? this.element.defaultPathColor;

    let seasonal: SeasonalColor | null = null;

    if (typeof rawPathColor === 'string') {
      const parts = rawPathColor
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);

      if (parts.length >= 2) {
        seasonal = { summer: parts[0], winter: parts[1] };
      } else if (parts.length === 1) {
        seasonal = { summer: parts[0], winter: parts[0] };
      }
    } else if (rawPathColor && typeof rawPathColor === 'object') {
      seasonal = rawPathColor;
    }

    let resolvedPathColor = '';
    if (seasonal) {
      const season = await firstValueFrom(this.layerService.season$);
      resolvedPathColor = season === Season.Winter ? seasonal.winter : seasonal.summer;
    }

    return status === AssetStatus.Open || status === AssetStatus.NoStatus || this.isMarked
      ? resolvedPathColor
      : '#808080cc';
  }

  private addEventListeners(): void {
    this.clickableCurve.on('click', (event: LeafletMouseEvent) => {
      this.hoverService.hover({
        guid: this.element.guid,
        hover: false,
        iconHovered: false,
        hoverPosition: { x: event.latlng.lat, y: event.latlng.lng },
      });
      this.mapStateService.clickElement(this.element, null);
    });

    this.clickableCurve.on('mouseover', (event: LeafletMouseEvent) => {
      this.hoverService.hover({
        guid: this.element.guid,
        hover: true,
        iconHovered: false,
        hoverPosition: { x: event.latlng.lat, y: event.latlng.lng },
      });
    });

    this.clickableCurve.on('mouseout', () => {
      this.hoverService.hover({ guid: this.element.guid, hover: false, iconHovered: false });
    });
  }

  private async zoom(zoom: SisZoom): Promise<void> {
    if (!zoom) {
      return;
    }

    const scaling = zoom.iconZoom;
    const pathWeight = (this.element.pathWeight ?? this.element.defaultPathWeight) * zoom.baseIconZoom;
    const width = Math.max(pathWeight * scaling, 1);
    const dashArray = this.element.pathDashArray?.split(' ').map((x) => +x * scaling);

    if (this.curveExists(this.visibleCurve)) {
      this.setStrokeWidth(this.visibleCurve, width);
      this.setStrokeDashArray(this.visibleCurve, dashArray);
      this.displayCurve(this.visibleCurve);
      await this.mapStyleService.zoomCurveEndMarkers(this.curveEndMarkers, scaling);
    }
    if (this.curveExists(this.backgroundCurve)) {
      this.setStrokeWidth(this.backgroundCurve, width);
      this.displayCurve(this.visibleCurve);
    }
    if (this.curveExists(this.highlightCurve)) {
      this.setStrokeWidth(this.highlightCurve, width + 5 * zoom.baseIconZoom * scaling);
      this.displayCurve(this.highlightCurve);
    }
    if (this.curveExists(this.clickableCurve)) {
      this.setStrokeWidth(this.clickableCurve, width + 16 * zoom.baseIconZoom);
      this.displayCurve(this.clickableCurve);
    }

    this.changeDetector.detectChanges();
  }

  private curveExists(curveElement: Curve): boolean {
    return curveElement && curveElement.getElement() != null;
  }

  private setStrokeWidth(curveElement: Curve, width: number): void {
    curveElement.getElement().setAttribute('stroke-width', width.toString());
  }

  private setStrokeOpacity(curveElement: Curve, opacity: number): void {
    curveElement.getElement().setAttribute('stroke-opacity', opacity.toString());
  }

  private setStrokeDashArray(curveElement: Curve, dashArray: number[]): void {
    curveElement.getElement().setAttribute('stroke-dasharray', dashArray?.join(' '));
  }

  private displayCurve(curveElement: Curve): void {
    (curveElement.getElement() as HTMLElement).style.cursor =
      this.editMode ||
      (!this.hoverService.hoverDisabled && !(this.element.disallowHighlight && this.element.disallowPopUp))
        ? 'pointer'
        : 'grab';
  }

  private showLayer(): void {
    if (this.assetsLayerGroup && !this.assetsLayerGroup.hasLayer(this.layerGroup)) {
      this.assetsLayerGroup.addLayer(this.layerGroup);
      this.updateCurve();
    }
  }

  private hideLayer(): void {
    if (this.assetsLayerGroup?.hasLayer(this.layerGroup)) {
      this.assetsLayerGroup.removeLayer(this.layerGroup);
    }
  }
}


